import {
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import {
  AlertCircle,
  ArrowRight,
  Brain,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Globe,
  GripVertical,
  Lightbulb,
  Mail,
  Quote,
  Scale as ScaleIcon,
  Send,
  Shield,
  TrendingUp,
  X,
} from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Bar,
  BarChart,
  CartesianGrid,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import jsPDF from "jspdf";
import { apiUrl } from "../config";
import questionsEn from "./questions.en";
import questionsCh from "./questions.ch";
import questionsTh from "./questions.th";
import questionsJa from "./questions.ja";
import feedbackEn from "./FeedbackMessages.en";
import feedbackCh from "./FeedbackMessages.ch";
import feedbackTh from "./FeedbackMessages.th";
import feedbackJa from "./FeedbackMessages.ja";
import deepDiveEn from "./DeepDiveContent.en";
import deepDiveCh from "./DeepDiveContent.ch";
import deepDiveTh from "./DeepDiveContent.th";
import deepDiveJa from "./DeepDiveContent.ja";
import kigumiLogo from "../assets/kigumi-logo.png";
import {
  DEFAULT_INVITE_CONFIG,
  INVITE_ROLE_LABELS,
  LANGUAGE_OPTIONS,
  RESULT_CATEGORY_LABELS,
  getExperienceCopy,
} from "./diagnosticExperienceContent";

const PDF_FILENAME = "DigitalWellbeingResults.pdf";
const PDF_PAGE_MARGIN_X = 16;
const PDF_PAGE_MARGIN_Y = 18;
const PDF_PAGE_FOOTER_Y = 287;
const PDF_PAGE_BREAK_THRESHOLD = 24;
const PDF_FONT_FILES = {
  en: {
    family: "NotoSansPdf",
    fileName: "NotoSans-Regular.ttf",
    publicPath: `${process.env.PUBLIC_URL || ""}/fonts/NotoSans-Regular.ttf`,
  },
  ch: {
    family: "NotoSansTCPdf",
    fileName: "NotoSansTC-Variable.ttf",
    publicPath: `${process.env.PUBLIC_URL || ""}/fonts/NotoSansTC-Variable.ttf`,
  },
  th: {
    family: "NotoSansThaiPdf",
    fileName: "NotoSansThai-Variable.ttf",
    publicPath: `${process.env.PUBLIC_URL || ""}/fonts/NotoSansThai-Variable.ttf`,
  },
  ja: {
    family: "NotoSansJPPdf",
    fileName: "NotoSansJP-Variable.ttf",
    publicPath: `${process.env.PUBLIC_URL || ""}/fonts/NotoSansJP-Variable.ttf`,
  },
};
const PDF_FONT_CACHE = new Map();
const PDF_COLORS = {
  teal: [13, 148, 136],
  tealSoft: [204, 251, 241],
  cyanSoft: [224, 242, 254],
  slate900: [15, 23, 42],
  slate700: [51, 65, 85],
  slate500: [100, 116, 139],
  slate300: [203, 213, 225],
  slate200: [226, 232, 240],
  white: [255, 255, 255],
};
const PDF_ACCENT_COLORS = [
  [20, 184, 166],
  [6, 182, 212],
  [59, 130, 246],
  [99, 102, 241],
];
const PDF_LOCALE_MAP = {
  en: "en-US",
  ch: "zh-TW",
  th: "th-TH",
  ja: "ja-JP",
};

const DIAGNOSTIC_BUNDLES = {
  en: {
    questions: questionsEn,
    feedbackMessages: feedbackEn,
    deepDiveContent: deepDiveEn,
  },
  ch: {
    questions: questionsCh,
    feedbackMessages: feedbackCh,
    deepDiveContent: {
      "\u5bb6\u5ead\u4e2d\u7684\u6578\u78bc\u5065\u5eb7\u74b0\u5883": deepDiveCh.family_env,
      "\u5bb6\u5ead\u5728\u6578\u78bc\u5065\u5eb7\u8207\u4eba\u5de5\u667a\u6167\u65b9\u9762\u7684\u6e9d\u901a":
        deepDiveCh.family_ai_comms,
      "\u7236\u6bcd\u7684\u61c9\u8b8a\u80fd\u529b\u8207\u81ea\u6211\u6548\u80fd\u611f": deepDiveCh.resourcefulness,
      "\u5b78\u6821\u8207\u5bb6\u9577\u6e9d\u901a": deepDiveCh.school_parent_comm,
    },
  },
  th: {
    questions: questionsTh,
    feedbackMessages: feedbackTh,
    deepDiveContent: deepDiveTh,
  },
  ja: {
    questions: questionsJa,
    feedbackMessages: feedbackJa,
    deepDiveContent: deepDiveJa,
  },
};

const RESULT_CARD_COLORS = [
  "from-teal-500 to-cyan-500",
  "from-cyan-500 to-blue-500",
  "from-blue-500 to-indigo-500",
  "from-indigo-500 to-violet-500",
];

function normalizeLanguage(value) {
  if (value === "zh-TW") return "ch";
  return LANGUAGE_OPTIONS.some((option) => option.code === value) ? value : "en";
}

function getRequestedLanguage(searchString, routeLanguage) {
  const browserSearch =
    typeof window !== "undefined" && window.location.search
      ? window.location.search
      : searchString;

  return normalizeLanguage(
    new URLSearchParams(browserSearch).get("lang") ||
      routeLanguage ||
      localStorage.getItem("lang") ||
      "en"
  );
}

function normalizeInviteCode(value) {
  return String(value || "").trim().toUpperCase();
}

function includesOther(value) {
  const text = String(value || "").toLowerCase();
  return (
    text.includes("other") ||
    text.includes("\u5176\u4ed6") ||
    text.includes("\u5176\u5b83") ||
    text.includes("\u305d\u306e\u4ed6") ||
    text.includes("\u0e2d\u0e37\u0e48\u0e19")
  );
}

function getQuestionHint(question, copy) {
  if (!question) return copy.assessment.helperDefault;
  if (question.type === "checkbox") return copy.assessment.helperCheckbox;
  if (question.type === "ranking") return copy.assessment.helperRanking;
  if (question.type === "grouped_likert") return copy.assessment.helperGrouped;
  if (question.type === "text") return copy.assessment.helperText;
  if (question.type === "likert" || question.type === "radio") {
    return copy.assessment.helperLikert;
  }
  return copy.assessment.helperDefault;
}

function getDisplayCategoryTitle(category, deepDiveContent) {
  if (!category) return "";
  return deepDiveContent?.[category]?.title || category;
}

function getCategoryChartLabel(category, language) {
  return RESULT_CATEGORY_LABELS[language]?.[category] || category;
}

function getSectionDescription(section, fallback) {
  if (section?.importance?.length) return section.importance[0];
  return fallback;
}

function buildFeedbackResult(finalScore, feedbackMessages) {
  if (!finalScore) return feedbackMessages[0] || null;
  const percentage = Math.round((Math.max(0, finalScore.score) / finalScore.max) * 100);
  return (
    feedbackMessages.find(
      (item) => percentage >= item.range[0] && percentage <= item.range[1]
    ) || feedbackMessages[0]
  );
}

function clampPercentage(score, max) {
  return Math.round((Math.max(0, score) / Math.max(max, 1)) * 100);
}

function getPdfLocale(language) {
  return PDF_LOCALE_MAP[language] || PDF_LOCALE_MAP.en;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = String(reader.result || "");
      const [, base64 = ""] = result.split(",");
      resolve(base64);
    };
    reader.onerror = () => reject(reader.error || new Error("Unable to read font"));
    reader.readAsDataURL(blob);
  });
}

async function loadPdfFont(language) {
  const fontConfig = PDF_FONT_FILES[language] || PDF_FONT_FILES.en;
  if (!PDF_FONT_CACHE.has(fontConfig.family)) {
    PDF_FONT_CACHE.set(
      fontConfig.family,
      (async () => {
        const response = await fetch(fontConfig.publicPath);
        if (!response.ok) {
          throw new Error(`Unable to load PDF font: ${fontConfig.fileName}`);
        }
        const base64 = await blobToBase64(await response.blob());
        return { ...fontConfig, base64 };
      })()
    );
  }

  return PDF_FONT_CACHE.get(fontConfig.family);
}

async function applyPdfFont(doc, language) {
  const fontConfig = await loadPdfFont(language);
  doc.addFileToVFS(fontConfig.fileName, fontConfig.base64);
  doc.addFont(fontConfig.fileName, fontConfig.family, "normal");
  doc.setFont(fontConfig.family, "normal");
  return fontConfig.family;
}

function buildReportCategories(categoryScores, deepDiveContent, feedback, language) {
  return Object.entries(categoryScores)
    .filter(([category]) => Boolean(deepDiveContent?.[category]))
    .map(([category, scoreData], index) => {
      const section = deepDiveContent?.[category];
      return {
        category,
        chartLabel: getCategoryChartLabel(category, language),
        title: getDisplayCategoryTitle(category, deepDiveContent),
        description: getSectionDescription(section, feedback?.message || ""),
        score: clampPercentage(scoreData.score, scoreData.max),
        accent: PDF_ACCENT_COLORS[index % PDF_ACCENT_COLORS.length],
      };
    });
}

function formatReportDate(language) {
  return new Intl.DateTimeFormat(getPdfLocale(language), {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(new Date());
}

async function buildReportPdfBlob({
  copy,
  language,
  userInfo,
  inviteConfig,
  finalScore,
  categoryScores,
  feedback,
  deepDiveContent,
}) {
  const doc = new jsPDF({ orientation: "p", unit: "mm", format: "a4", compress: true });
  const fontFamily = await applyPdfFont(doc, language);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - PDF_PAGE_MARGIN_X * 2;
  const roleLabels = INVITE_ROLE_LABELS[language] || INVITE_ROLE_LABELS.en;
  const categories = buildReportCategories(
    categoryScores,
    deepDiveContent,
    feedback,
    language
  );
  const overallScore = clampPercentage(finalScore.score, finalScore.max);
  const footerLabel = copy.results.brand;
  const state = { page: 1, y: PDF_PAGE_MARGIN_Y };

  const setTextStyle = (size, color = PDF_COLORS.slate700) => {
    doc.setFont(fontFamily, "normal");
    doc.setFontSize(size);
    doc.setTextColor(...color);
  };

  const drawFooter = () => {
    setTextStyle(8, PDF_COLORS.slate500);
    doc.text(footerLabel, PDF_PAGE_MARGIN_X, PDF_PAGE_FOOTER_Y);
    doc.text(String(state.page), pageWidth - PDF_PAGE_MARGIN_X, PDF_PAGE_FOOTER_Y, {
      align: "right",
    });
  };

  const addPage = () => {
    drawFooter();
    doc.addPage();
    state.page += 1;
    state.y = PDF_PAGE_MARGIN_Y;

    setTextStyle(10, PDF_COLORS.teal);
    doc.text(copy.results.brand, PDF_PAGE_MARGIN_X, state.y);
    state.y += 6;
    doc.setDrawColor(...PDF_COLORS.slate200);
    doc.line(
      PDF_PAGE_MARGIN_X,
      state.y,
      pageWidth - PDF_PAGE_MARGIN_X,
      state.y
    );
    state.y += 6;
  };

  const ensureSpace = (heightNeeded) => {
    if (state.y + heightNeeded <= pageHeight - PDF_PAGE_BREAK_THRESHOLD) {
      return;
    }
    addPage();
  };

  const writeParagraph = (
    text,
    {
      size = 11,
      color = PDF_COLORS.slate700,
      lineHeight = 5.4,
      gapAfter = 4,
      x = PDF_PAGE_MARGIN_X,
      width = contentWidth,
    } = {}
  ) => {
    if (!text) return;
    const lines = doc.splitTextToSize(String(text).trim(), width);
    ensureSpace(lines.length * lineHeight + gapAfter);
    setTextStyle(size, color);
    doc.text(lines, x, state.y);
    state.y += lines.length * lineHeight + gapAfter;
  };

  const writeKeyValue = (label, value, { size = 10 } = {}) => {
    const displayValue = String(value || "").trim();
    if (!displayValue) return;
    writeParagraph(`${label}: ${displayValue}`, {
      size,
      color: PDF_COLORS.slate700,
      gapAfter: 2,
    });
  };

  doc.setFillColor(...PDF_COLORS.teal);
  doc.rect(0, 0, pageWidth, 42, "F");
  setTextStyle(11, PDF_COLORS.white);
  doc.text(copy.results.brand, PDF_PAGE_MARGIN_X, 12);
  setTextStyle(22, PDF_COLORS.white);
  doc.text(
    doc.splitTextToSize(copy.results.title, contentWidth),
    PDF_PAGE_MARGIN_X,
    22
  );
  setTextStyle(10, PDF_COLORS.white);
  doc.text(formatReportDate(language), PDF_PAGE_MARGIN_X, 36);

  state.y = 50;

  doc.setDrawColor(...PDF_COLORS.slate200);
  doc.setFillColor(...PDF_COLORS.white);
  doc.roundedRect(PDF_PAGE_MARGIN_X, state.y, contentWidth, 26, 4, 4, "FD");
  state.y += 8;
  setTextStyle(10, PDF_COLORS.slate500);
  doc.text(copy.form.fullName, PDF_PAGE_MARGIN_X + 6, state.y);
  doc.text(copy.form.email, pageWidth / 2, state.y);
  state.y += 6;
  setTextStyle(12, PDF_COLORS.slate900);
  doc.text(userInfo.fullName || "-", PDF_PAGE_MARGIN_X + 6, state.y);
  doc.text(userInfo.email || "-", pageWidth / 2, state.y);
  state.y += 8;
  setTextStyle(10, PDF_COLORS.slate500);
  doc.text(copy.form.role, PDF_PAGE_MARGIN_X + 6, state.y);
  state.y += 6;
  setTextStyle(12, PDF_COLORS.slate900);
  doc.text(roleLabels[userInfo.role] || userInfo.role || "-", PDF_PAGE_MARGIN_X + 6, state.y);
  state.y += 12;

  if (userInfo.studentClass) {
    writeKeyValue(copy.form.studentClass, userInfo.studentClass, { size: 10 });
  }
  if (inviteConfig?.organization) {
    writeKeyValue("Organization", inviteConfig.organization, { size: 10 });
  }
  if (inviteConfig?.campaign) {
    writeKeyValue("Campaign", inviteConfig.campaign, { size: 10 });
  }

  const scoreDescriptionLines = doc.splitTextToSize(
    copy.results.overallScoreDesc,
    contentWidth - 80
  );
  const scoreCardHeight = Math.max(28, 14 + scoreDescriptionLines.length * 5);

  ensureSpace(scoreCardHeight);
  doc.setFillColor(...PDF_COLORS.tealSoft);
  doc.roundedRect(PDF_PAGE_MARGIN_X, state.y, contentWidth, scoreCardHeight, 5, 5, "F");
  setTextStyle(11, PDF_COLORS.teal);
  doc.text(copy.results.overallScore, PDF_PAGE_MARGIN_X + 6, state.y + 8);
  setTextStyle(24, PDF_COLORS.slate900);
  doc.text(`${overallScore}${copy.results.outOf}`, PDF_PAGE_MARGIN_X + 6, state.y + 20);
  setTextStyle(10, PDF_COLORS.slate700);
  doc.text(scoreDescriptionLines, PDF_PAGE_MARGIN_X + 78, state.y + 10);
  state.y += scoreCardHeight + 8;

  setTextStyle(14, PDF_COLORS.slate900);
  doc.text(copy.results.categoryBreakdown, PDF_PAGE_MARGIN_X, state.y);
  state.y += 8;

  categories.forEach((category) => {
    ensureSpace(14);
    setTextStyle(10, PDF_COLORS.slate700);
    doc.text(category.title, PDF_PAGE_MARGIN_X, state.y);
    doc.text(
      `${category.score}${copy.results.outOf}`,
      pageWidth - PDF_PAGE_MARGIN_X,
      state.y,
      { align: "right" }
    );
    state.y += 3;
    doc.setFillColor(...PDF_COLORS.slate200);
    doc.roundedRect(PDF_PAGE_MARGIN_X, state.y, contentWidth, 4, 2, 2, "F");
    doc.setFillColor(...category.accent);
    doc.roundedRect(
      PDF_PAGE_MARGIN_X,
      state.y,
      (contentWidth * Math.max(category.score, 2)) / 100,
      4,
      2,
      2,
      "F"
    );
    state.y += 9;
  });

  writeParagraph(feedback?.message, {
    size: 11,
    color: PDF_COLORS.slate700,
    lineHeight: 5.6,
    gapAfter: 6,
  });

  if (feedback?.actions?.length) {
    ensureSpace(18);
    setTextStyle(14, PDF_COLORS.slate900);
    doc.text(copy.results.nextStepsTitle, PDF_PAGE_MARGIN_X, state.y);
    state.y += 7;
    feedback.actions.forEach((action, index) => {
      writeParagraph(`${index + 1}. ${action.text}`, {
        size: 10.5,
        color: PDF_COLORS.slate700,
        lineHeight: 5.2,
        gapAfter: 1.5,
      });
      writeParagraph(`${action.label}: ${action.href}`, {
        size: 9.5,
        color: PDF_COLORS.teal,
        lineHeight: 4.8,
        gapAfter: 3.5,
      });
    });
  }

  categories.forEach((category) => {
    ensureSpace(36);
    setTextStyle(14, PDF_COLORS.slate900);
    doc.text(category.title, PDF_PAGE_MARGIN_X, state.y);
    setTextStyle(11, category.accent);
    doc.text(
      `${category.score}${copy.results.outOf}`,
      pageWidth - PDF_PAGE_MARGIN_X,
      state.y,
      { align: "right" }
    );
    state.y += 8;
    writeParagraph(category.description, {
      size: 10.5,
      color: PDF_COLORS.slate700,
      lineHeight: 5.5,
      gapAfter: 4,
    });
  });

  drawFooter();
  return doc.output("blob");
}

async function downloadPdfBlob(reportData) {
  const pdfBlob = await buildReportPdfBlob(reportData);
  const pdfUrl = window.URL.createObjectURL(pdfBlob);
  const anchor = document.createElement("a");
  anchor.href = pdfUrl;
  anchor.download = PDF_FILENAME;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 1000);
}

function Footer({ copy }) {
  const [email, setEmail] = useState("");
  const [agreedToMarketing, setAgreedToMarketing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const handleSubscribe = (event) => {
    event.preventDefault();
    if (!email || !agreedToMarketing) return;
    setSubmittedEmail(email);
    setShowConfirmation(true);
    setEmail("");
    setAgreedToMarketing(false);
  };

  return (
    <footer className="w-full border-t border-slate-200 bg-slate-50">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="mb-6 text-center">
          <h3 className="mb-2 text-lg font-semibold text-slate-800 sm:text-xl">
            {copy.footer.stayUpdated}
          </h3>
          <p className="mb-4 text-sm text-slate-600">{copy.footer.description}</p>
          <form onSubmit={handleSubscribe} className="mx-auto max-w-md">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row">
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder={copy.footer.placeholder}
                required
                className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm text-slate-700 outline-none transition-all focus:border-orange-500 focus:ring-2 focus:ring-orange-500/30"
              />
              <button
                type="submit"
                disabled={!agreedToMarketing}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-pink-500 px-6 py-2.5 text-sm font-medium text-white transition-all hover:from-orange-600 hover:to-pink-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {copy.footer.subscribe}
                <Send className="h-4 w-4" />
              </button>
            </div>
            <label className="flex items-start gap-2 text-left">
              <input
                type="checkbox"
                checked={agreedToMarketing}
                onChange={(event) => setAgreedToMarketing(event.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-xs text-slate-600">{copy.footer.marketingConsent}</span>
            </label>
          </form>
        </div>

        <div className="border-t border-slate-200 pt-6">
          <div className="mb-4 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-6">
            <a
              href="https://www.kigumigroup.com/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-slate-600 transition-colors hover:text-orange-600"
            >
              <Globe className="h-4 w-4" />
              <span>www.kigumigroup.com</span>
            </a>
            <a
              href="https://www.instagram.com/kigumigroup"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm text-slate-600 transition-colors hover:text-pink-600"
            >
              <ExternalLink className="h-4 w-4" />
              <span>{copy.footer.followUs}</span>
            </a>
          </div>

          <p className="text-center text-xs text-slate-500 sm:text-sm">
            {copy.footer.copyright}
          </p>
        </div>
      </div>

      <AnimatePresence>
        {showConfirmation ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={() => setShowConfirmation(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(event) => event.stopPropagation()}
              className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl sm:p-8"
            >
              <button
                type="button"
                onClick={() => setShowConfirmation(false)}
                className="absolute right-4 top-4 text-slate-400 transition-colors hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
              <div className="mb-4 flex justify-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-pink-500">
                  <CheckCircle2 className="h-9 w-9 text-white" />
                </div>
              </div>
              <h3 className="mb-3 text-center text-xl font-bold text-slate-800 sm:text-2xl">
                {copy.footer.confirmationTitle}
              </h3>
              <p className="mb-2 text-center text-slate-600">
                {copy.footer.confirmationMessage}
              </p>
              <p className="mb-6 text-center font-semibold text-orange-600">
                {submittedEmail}
              </p>
              <button
                type="button"
                onClick={() => setShowConfirmation(false)}
                className="w-full rounded-xl bg-gradient-to-r from-orange-500 to-pink-500 py-3 font-medium text-white transition-all hover:from-orange-600 hover:to-pink-600"
              >
                {copy.footer.confirmationButton}
              </button>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </footer>
  );
}

function WelcomeScreen({
  copy,
  language,
  setLanguage,
  onContinue,
  isInviteEntry,
  inviteStatus,
  inviteError,
  disableContinue,
}) {
  const languageValue = language === "ch" ? "zh-TW" : language;
  const inviteStatusTone =
    inviteStatus === "resolved"
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : inviteStatus === "error"
        ? "border-rose-200 bg-rose-50 text-rose-700"
        : "border-slate-200 bg-slate-50 text-slate-600";

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      <header className="w-full border-b border-slate-200 bg-white/90 px-4 py-4 backdrop-blur-sm sm:px-6 sm:py-6">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={kigumiLogo} alt="Kigumi Group" className="h-8 w-auto sm:h-10" />
          </div>

          <div className="flex items-center gap-2">
            <Globe className="hidden h-4 w-4 text-slate-500 sm:block" />
            <select
              value={languageValue}
              onChange={(event) =>
                setLanguage(normalizeLanguage(event.target.value))
              }
              className="cursor-pointer rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition-all hover:border-teal-400 focus:border-transparent focus:ring-2 focus:ring-teal-500"
            >
              {LANGUAGE_OPTIONS.map((option) => (
                <option key={option.code} value={option.code === "ch" ? "zh-TW" : option.code}>
                  {option.flag} {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </header>

      <main className="px-4 py-8 sm:px-6 sm:py-12">
        <div className="mx-auto max-w-5xl">
          <motion.div
            key={language}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 text-center sm:mb-12"
          >
            <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-100 sm:h-20 sm:w-20 sm:rounded-3xl">
              <Brain className="h-8 w-8 text-teal-600 sm:h-10 sm:w-10" />
            </div>
            <h1 className="mb-4 text-3xl font-bold leading-tight text-slate-800 sm:text-4xl md:text-5xl lg:text-6xl">
              {copy.welcome.title}
              <br />
              <span className="bg-gradient-to-r from-teal-500 to-cyan-500 bg-clip-text text-transparent">
                {copy.welcome.titleHighlight}
              </span>
            </h1>
            <p className="mx-auto max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg md:text-xl">
              {copy.welcome.subtitle}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-6 rounded-2xl bg-white p-6 shadow-lg shadow-slate-200/50 sm:mb-8 sm:rounded-3xl sm:p-8 md:p-10"
          >
            <h2 className="mb-4 text-2xl font-bold text-slate-800 sm:text-3xl md:text-4xl">
              {copy.welcome.assessmentTitle}
            </h2>
            <p className="mb-4 text-sm leading-relaxed text-slate-600 sm:text-base md:text-lg">
              {copy.welcome.assessmentDescription}
            </p>
            <p className="mb-6 text-xs text-slate-500 sm:text-sm">
              {copy.welcome.assessmentLanguages}
            </p>
            {isInviteEntry ? (
              <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                      {copy.landing.invitationLink}
                    </p>
                    <p className="mt-1 text-sm font-semibold text-slate-800 sm:text-base">
                      {inviteStatus === "resolved"
                        ? copy.landing.invitationLinkConfirmed
                        : inviteStatus === "error"
                          ? inviteError || copy.landing.inviteCodeInvalid
                          : copy.landing.invitationLinkLoading}
                    </p>
                  </div>
                  <div
                    className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${inviteStatusTone}`}
                  >
                    {inviteStatus === "resolved"
                      ? copy.landing.invitationLinkConfirmed
                      : inviteStatus === "error"
                        ? inviteError || copy.landing.inviteCodeInvalid
                        : copy.landing.invitationLinkLoading}
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-600">
                  {inviteStatus === "error"
                    ? inviteError || copy.landing.inviteCodeInvalid
                    : copy.landing.invitationLinkNote}
                </p>
              </div>
            ) : null}
            <button
              type="button"
              onClick={onContinue}
              disabled={disableContinue}
              className="inline-flex w-full items-center justify-center gap-3 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 px-8 py-4 text-base font-semibold text-white transition-all hover:from-teal-600 hover:to-cyan-600 disabled:cursor-not-allowed disabled:from-slate-300 disabled:to-slate-300 disabled:text-white/90 sm:w-auto"
            >
              {copy.welcome.startAssessmentButton}
              <ArrowRight className="h-5 w-5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-6 rounded-2xl border border-teal-100 bg-gradient-to-br from-teal-50 to-cyan-50 p-6 shadow-md sm:mb-8 sm:rounded-3xl sm:p-8 md:p-10"
          >
            <div className="flex items-start gap-3">
              <Quote className="h-8 w-8 shrink-0 text-teal-600 sm:h-10 sm:w-10" />
              <div>
                <h3 className="mb-3 text-lg font-semibold text-slate-800 sm:text-xl">
                  {copy.welcome.testimonyTitle}
                </h3>
                <p className="mb-4 text-sm italic leading-relaxed text-slate-700 sm:text-base">
                  "{copy.welcome.testimonyQuote}"
                </p>
                <p className="text-sm font-medium text-slate-600">
                  {copy.welcome.testimonyAuthor}
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mb-6 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 p-6 text-white shadow-xl sm:mb-8 sm:rounded-3xl sm:p-8 md:p-10"
          >
            <h2 className="mb-4 text-xl font-bold sm:text-2xl md:text-3xl">
              {copy.welcome.kigumiTitle}
            </h2>
            <p className="mb-6 text-sm leading-relaxed text-slate-200 sm:text-base md:text-lg">
              {copy.welcome.kigumiDescription}
            </p>
            <div className="border-t border-slate-700 pt-6">
              <h3 className="mb-4 text-lg font-semibold sm:text-xl">
                {copy.welcome.productsTitle}
              </h3>
              <div className="grid gap-4 sm:grid-cols-3">
                <a
                  href="https://kigulab.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="group rounded-xl bg-slate-700/50 p-4 transition-all hover:bg-slate-700"
                >
                  <div className="mb-2 flex items-start justify-between">
                    <h4 className="font-semibold text-white transition-colors group-hover:text-teal-400">
                      {copy.welcome.product1Name}
                    </h4>
                    <ExternalLink className="h-4 w-4 text-slate-400 transition-colors group-hover:text-teal-400" />
                  </div>
                  <p className="text-xs text-slate-300 sm:text-sm">
                    {copy.welcome.product1Desc}
                  </p>
                </a>
                <a
                  href="https://digital-parenting-assistant.kigulab.com/"
                  target="_blank"
                  rel="noreferrer"
                  className="group rounded-xl bg-slate-700/50 p-4 transition-all hover:bg-slate-700"
                >
                  <div className="mb-2 flex items-start justify-between">
                    <h4 className="font-semibold text-white transition-colors group-hover:text-teal-400">
                      {copy.welcome.product2Name}
                    </h4>
                    <ExternalLink className="h-4 w-4 text-slate-400 transition-colors group-hover:text-teal-400" />
                  </div>
                  <p className="text-xs text-slate-300 sm:text-sm">
                    {copy.welcome.product2Desc}
                  </p>
                </a>
                <div className="rounded-xl border border-teal-500/30 bg-gradient-to-br from-teal-500/20 to-cyan-500/20 p-4">
                  <h4 className="mb-2 font-semibold text-teal-300">
                    {copy.welcome.product3Name}
                  </h4>
                  <p className="text-xs text-slate-300 sm:text-sm">
                    {copy.welcome.product3Desc}
                  </p>
                </div>
              </div>
              <p className="mt-6 text-sm italic text-slate-400">
                {copy.welcome.followUs}
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-center"
          >
            <button
              type="button"
              onClick={onContinue}
              disabled={disableContinue}
              className="inline-flex items-center gap-3 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 px-8 py-4 text-base font-semibold text-white transition-all hover:from-teal-600 hover:to-cyan-600 disabled:cursor-not-allowed disabled:from-slate-300 disabled:to-slate-300 disabled:text-white/90 sm:px-12 sm:py-5 sm:text-lg"
            >
              {copy.welcome.startButton}
              <ArrowRight className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
            <p className="mt-4 text-sm text-slate-500">{copy.welcome.languageNote}</p>
          </motion.div>
        </div>
      </main>

      <Footer copy={copy} />
    </div>
  );
}

function BasicInfoForm({
  copy,
  language,
  userInfo,
  formErrors,
  inviteStatus,
  inviteError,
  inviteCodeLocked,
  roleLocked,
  requireInviteCode,
  onBack,
  onChange,
  onSubmit,
}) {
  const roleLabels = INVITE_ROLE_LABELS[language] || INVITE_ROLE_LABELS.en;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      <header className="w-full border-b border-slate-200 bg-white/90 px-4 py-4 backdrop-blur-sm sm:px-6">
        <div className="mx-auto flex max-w-2xl items-center justify-between">
          <img src={kigumiLogo} alt="Kigumi Group" className="h-5 w-auto sm:h-6" />
          <div className="text-xs text-slate-500 sm:text-sm">
            <span className="font-medium text-slate-700">
              {inviteCodeLocked
                ? copy.form.inviteLinkConfirmedTitle
                : userInfo.inviteCode || copy.landing.missingInviteCode}
            </span>
          </div>
        </div>
      </header>

      <main className="flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-2xl"
        >
          <div className="mb-6 text-center sm:mb-8">
            <h1 className="mb-2 text-2xl font-bold text-slate-800 sm:text-3xl md:text-4xl">
              {copy.form.title}
            </h1>
            <p className="text-sm text-slate-600 sm:text-base">{copy.form.subtitle}</p>
          </div>

          <form
            onSubmit={(event) => {
              event.preventDefault();
              onSubmit();
            }}
            className="rounded-2xl bg-white p-6 shadow-lg shadow-slate-200/50 sm:rounded-3xl sm:p-8"
          >
            <div className="mb-6 space-y-5 sm:mb-8 sm:space-y-6">
              <FormField
                label={copy.form.fullName}
                error={formErrors.fullName}
                htmlFor="fullName"
                required
              >
                <input
                  id="fullName"
                  type="text"
                  value={userInfo.fullName}
                  onChange={(event) => onChange("fullName", event.target.value)}
                  placeholder={copy.form.placeholders.fullName}
                  className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-base text-slate-700 outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-teal-500 sm:rounded-2xl sm:py-4"
                />
              </FormField>

              <FormField
                label={copy.form.email}
                error={formErrors.email}
                htmlFor="email"
                required
              >
                <input
                  id="email"
                  type="email"
                  value={userInfo.email}
                  onChange={(event) => onChange("email", event.target.value)}
                  placeholder={copy.form.placeholders.email}
                  className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-base text-slate-700 outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-teal-500 sm:rounded-2xl sm:py-4"
                />
              </FormField>

              <FormField label={copy.form.role} htmlFor="role">
                <select
                  id="role"
                  value={userInfo.role}
                  onChange={(event) => onChange("role", event.target.value)}
                  disabled={roleLocked}
                  className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base text-slate-700 outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-teal-500 disabled:bg-slate-50 disabled:text-slate-500 sm:rounded-2xl sm:py-4"
                >
                  <option value="school">{roleLabels.school}</option>
                  <option value="parent">{roleLabels.parent}</option>
                  <option value="student">{roleLabels.student}</option>
                </select>
                {roleLocked ? (
                  <p className="mt-1.5 text-xs text-slate-500">{copy.form.roleLocked}</p>
                ) : null}
              </FormField>

              {inviteCodeLocked ? (
                <div className="rounded-xl border-2 border-emerald-100 bg-emerald-50 px-4 py-4 sm:rounded-2xl">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                    <div>
                      <p className="text-sm font-semibold text-emerald-900">
                        {copy.form.inviteLinkConfirmedTitle}
                      </p>
                      <p className="mt-1 text-xs leading-relaxed text-emerald-800 sm:text-sm">
                        {inviteStatus === "loading"
                          ? copy.landing.invitationLinkLoading
                          : copy.form.inviteLinkConfirmedNote}
                      </p>
                      {inviteError ? (
                        <p className="mt-1.5 text-xs text-rose-600">{inviteError}</p>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : (
                <FormField
                  label={copy.form.inviteCode}
                  error={requireInviteCode ? formErrors.inviteCode : ""}
                  htmlFor="inviteCode"
                >
                  <input
                    id="inviteCode"
                    type="text"
                    value={userInfo.inviteCode}
                    onChange={(event) => onChange("inviteCode", event.target.value)}
                    placeholder={copy.form.placeholders.inviteCode}
                    className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 font-mono text-base text-slate-700 outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-teal-500 sm:rounded-2xl sm:py-4"
                  />
                  <p className="mt-1.5 text-xs text-slate-500">
                    {copy.form.inviteCodeNote}
                  </p>
                  {inviteStatus === "loading" ? (
                    <p className="mt-1.5 text-xs text-slate-500">
                      {copy.landing.inviteCodeLoading}
                    </p>
                  ) : null}
                  {inviteStatus === "resolved" ? (
                    <p className="mt-1.5 text-xs font-medium text-emerald-600">
                      {copy.landing.inviteCodeConfirmed}
                    </p>
                  ) : null}
                  {inviteError ? (
                    <p className="mt-1.5 text-xs text-rose-600">{inviteError}</p>
                  ) : null}
                </FormField>
              )}

              {userInfo.role === "parent" || userInfo.role === "student" ? (
                <FormField label={copy.form.studentClass} htmlFor="studentClass">
                  <input
                    id="studentClass"
                    type="text"
                    value={userInfo.studentClass}
                    onChange={(event) => onChange("studentClass", event.target.value)}
                    placeholder={copy.form.placeholders.studentClass}
                    className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-base text-slate-700 outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-teal-500 sm:rounded-2xl sm:py-4"
                  />
                </FormField>
              ) : null}
            </div>

            <div className="mb-6 space-y-4 border-b border-slate-200 pb-6 sm:mb-8 sm:pb-8">
              <FormField label={copy.form.language} htmlFor="language">
                <select
                  id="language"
                  value={userInfo.language}
                  onChange={(event) => onChange("language", event.target.value)}
                  className="w-full rounded-xl border-2 border-slate-200 bg-white px-4 py-3 text-base text-slate-700 outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-teal-500 sm:rounded-2xl sm:py-4"
                >
                  {LANGUAGE_OPTIONS.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.flag} {option.label}
                    </option>
                  ))}
                </select>
              </FormField>

              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={userInfo.termsAccepted}
                  onChange={(event) => onChange("termsAccepted", event.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-teal-500 focus:ring-teal-500"
                />
                <span className="text-sm leading-6 text-slate-600">
                  {copy.form.terms}{" "}
                  <a
                    href="https://docs.google.com/document/d/1c5cTm1cBSGRJEV0Lc6AdO-5RlM36_TwsMbWosu9gcIc/edit?usp=sharing"
                    target="_blank"
                    rel="noreferrer"
                    className="font-medium text-slate-950 underline decoration-teal-300 decoration-2 underline-offset-4"
                  >
                    Terms and Conditions
                  </a>
                </span>
              </label>
              {formErrors.terms ? (
                <p className="text-sm text-rose-600">{formErrors.terms}</p>
              ) : null}
            </div>

            {requireInviteCode && inviteStatus !== "resolved" ? (
              <p className="mb-4 text-sm text-rose-600">{copy.form.submitDisabledInvite}</p>
            ) : null}

            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between">
              <button
                type="button"
                onClick={onBack}
                className="inline-flex items-center justify-center rounded-xl border-2 border-slate-300 px-5 py-3 text-sm font-medium text-slate-700 transition-all hover:bg-slate-50 sm:rounded-2xl sm:px-6 sm:py-4 sm:text-base"
              >
                {copy.form.backButton}
              </button>
              <button
                type="submit"
                disabled={requireInviteCode && inviteStatus !== "resolved"}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-4 text-base font-semibold text-white transition-all hover:from-teal-600 hover:to-cyan-600 disabled:cursor-not-allowed disabled:opacity-50 sm:rounded-2xl sm:py-5 sm:text-lg"
              >
                {copy.form.submitButton}
                <ArrowRight className="h-5 w-5" />
              </button>
            </div>
          </form>
        </motion.div>
      </main>

      <Footer copy={copy} />
    </div>
  );
}

function FormField({ label, error, required = false, htmlFor, children }) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="mb-2 block text-sm font-medium text-slate-700 sm:text-base"
      >
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>
      {children}
      {error ? <p className="mt-1.5 text-xs text-rose-600 sm:text-sm">{error}</p> : null}
    </div>
  );
}

function QuestionChoiceButton({ active, multi = false, label, onClick }) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      className={`w-full rounded-xl border-2 px-4 py-3.5 text-left transition-all sm:rounded-2xl sm:px-5 sm:py-4 ${
        active
          ? "border-orange-500 bg-orange-50 shadow-md"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`flex h-5 w-5 shrink-0 items-center justify-center border-2 transition-all sm:h-6 sm:w-6 ${
            multi ? "rounded-md" : "rounded-full"
          } ${active ? "border-orange-600 bg-orange-600" : "border-slate-300"}`}
        >
          {active ? <Check className="h-3 w-3 text-white sm:h-4 sm:w-4" /> : null}
        </div>
        <span
          className={`text-sm leading-relaxed sm:text-base ${
            active ? "font-medium text-slate-800" : "text-slate-700"
          }`}
        >
          {label}
        </span>
      </div>
    </motion.button>
  );
}

function SortableRankingItem({ id, index }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      {...attributes}
      {...listeners}
      className="flex cursor-move touch-manipulation items-center gap-3 rounded-xl border-2 border-slate-200 bg-white px-4 py-3.5 transition-all hover:border-slate-300 hover:shadow-md sm:rounded-2xl sm:px-5 sm:py-4"
    >
      <GripVertical className="h-5 w-5 text-slate-400" />
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-teal-100 to-cyan-100 sm:h-9 sm:w-9">
        <span className="text-sm font-semibold text-teal-700 sm:text-base">
          {index + 1}
        </span>
      </div>
      <span className="flex-1 text-sm leading-relaxed text-slate-700 sm:text-base">
        {id}
      </span>
    </div>
  );
}

function AssessmentScreen({
  copy,
  currentQuestion,
  currentIndex,
  totalQuestions,
  answers,
  error,
  onBack,
  onNext,
  onComplete,
  onAnswerChange,
  isSubmitting = false,
}) {
  const sensors = useSensors(useSensor(PointerSensor));
  const isCompleteStep = currentIndex === totalQuestions - 1;

  const renderOtherInput = () => {
    if (!currentQuestion) return null;
    if (currentQuestion.type === "checkbox") {
      const selected = answers[currentQuestion.name] || [];
      if (!selected.some((value) => includesOther(value))) return null;
    }
    if (currentQuestion.type === "radio") {
      if (!includesOther(answers[currentQuestion.name])) return null;
    }
    return (
      <input
        type="text"
        value={answers[`${currentQuestion.name}_other`] || ""}
        onChange={(event) =>
          onAnswerChange(`${currentQuestion.name}_other`, event.target.value)
        }
        placeholder={copy.assessment.otherPlaceholder}
        className="mt-4 w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-base text-slate-700 outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-orange-500 sm:rounded-2xl"
      />
    );
  };

  const renderQuestionBody = () => {
    if (currentQuestion.type === "radio") {
      return (
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => {
            const value = normalizeOptionValue(option);
            return (
              <QuestionChoiceButton
                key={`${currentQuestion.name}-${index}`}
                active={answers[currentQuestion.name] === value}
                label={normalizeOptionLabel(option)}
                onClick={() => onAnswerChange(currentQuestion.name, value)}
              />
            );
          })}
          {renderOtherInput()}
        </div>
      );
    }

    if (currentQuestion.type === "checkbox") {
      const selected = answers[currentQuestion.name] || [];
      return (
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => {
            const value = normalizeOptionValue(option);
            const nextSelected = selected.includes(value)
              ? selected.filter((item) => item !== value)
              : [...selected, value];

            return (
              <QuestionChoiceButton
                key={`${currentQuestion.name}-${index}`}
                active={selected.includes(value)}
                multi
                label={normalizeOptionLabel(option)}
                onClick={() => onAnswerChange(currentQuestion.name, nextSelected)}
              />
            );
          })}
          {renderOtherInput()}
        </div>
      );
    }

    if (currentQuestion.type === "likert") {
      return (
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => (
            <QuestionChoiceButton
              key={`${currentQuestion.name}-${index}`}
              active={answers[currentQuestion.name] === index}
              label={normalizeOptionLabel(option)}
              onClick={() => onAnswerChange(currentQuestion.name, index)}
            />
          ))}
        </div>
      );
    }

    if (currentQuestion.type === "grouped_likert") {
      return (
        <div className="space-y-6">
          {currentQuestion.subQuestions.map((subQuestion, subIndex) => (
            <div
              key={subQuestion.name}
              className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 sm:rounded-2xl"
            >
              <div className="mb-4 flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-950 text-xs font-semibold text-white">
                  {subIndex + 1}
                </div>
                <p className="text-sm leading-6 text-slate-700 sm:text-base">
                  {subQuestion.label}
                </p>
              </div>
              <div className="space-y-3">
                {currentQuestion.options.map((option, optionIndex) => (
                  <QuestionChoiceButton
                    key={`${subQuestion.name}-${optionIndex}`}
                    active={answers[subQuestion.name] === optionIndex}
                    label={normalizeOptionLabel(option)}
                    onClick={() => onAnswerChange(subQuestion.name, optionIndex)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (currentQuestion.type === "ranking") {
      const items = Array.isArray(answers[currentQuestion.name])
        ? answers[currentQuestion.name]
        : [...currentQuestion.options];
      return (
        <div className="space-y-3">
          <p className="mb-4 text-xs text-slate-600 sm:text-sm">
            {copy.assessment.dragInstruction}
          </p>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={({ active, over }) => {
              if (!over || active.id === over.id) return;
              const oldIndex = items.indexOf(active.id);
              const newIndex = items.indexOf(over.id);
              onAnswerChange(currentQuestion.name, arrayMove(items, oldIndex, newIndex));
            }}
          >
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
              {items.map((item, index) => (
                <SortableRankingItem
                  key={normalizeOptionLabel(item)}
                  id={normalizeOptionLabel(item)}
                  index={index}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>
      );
    }

    return (
      <input
        type="text"
        value={answers[currentQuestion.name] || ""}
        onChange={(event) => onAnswerChange(currentQuestion.name, event.target.value)}
        className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-base text-slate-700 outline-none transition-all focus:border-transparent focus:ring-2 focus:ring-orange-500 sm:rounded-2xl sm:px-5 sm:py-4"
      />
    );
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-10 w-full border-b border-slate-200 bg-white/90 px-4 py-4 backdrop-blur-sm sm:px-6 sm:py-6">
        <div className="mx-auto flex max-w-4xl items-center justify-between">
          <img src={kigumiLogo} alt="Kigumi Group" className="h-5 w-auto sm:h-6" />
          <div className="text-xs text-slate-500 sm:text-sm">
            <span className="font-medium text-slate-700">
              {currentIndex + 1} / {totalQuestions}
            </span>
          </div>
        </div>
      </header>

      <main className="px-4 py-6 sm:px-6 sm:py-8">
        <motion.div
          key={currentQuestion.name}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="mx-auto w-full max-w-3xl"
        >
          <div className="rounded-2xl bg-white p-6 shadow-lg shadow-slate-200/50 sm:rounded-3xl sm:p-8 md:p-12">
            <div className="mb-6 sm:mb-8">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] text-teal-700">
                {getDisplayCategoryTitle(currentQuestion.category, null)}
              </p>
              <h2 className="text-xl font-semibold leading-tight text-slate-800 sm:text-2xl md:text-3xl">
                {currentQuestion.question}
              </h2>
              <p className="mt-4 text-sm leading-6 text-slate-500 sm:text-base">
                {getQuestionHint(currentQuestion, copy)}
              </p>
            </div>

            <div className="mb-6 sm:mb-8">{renderQuestionBody()}</div>

            {error ? (
              <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                {error}
              </div>
            ) : null}

            <div className="flex gap-3">
              {currentIndex > 0 ? (
                <button
                  type="button"
                  onClick={onBack}
                  className="inline-flex items-center gap-2 rounded-xl border-2 border-slate-300 px-5 py-3 text-sm font-medium text-slate-700 transition-all hover:bg-slate-50 sm:rounded-2xl sm:px-6 sm:py-4 sm:text-base"
                >
                  <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span className="hidden sm:inline">{copy.assessment.backButton}</span>
                </button>
              ) : null}
              <button
                type="button"
                onClick={isCompleteStep ? onComplete : onNext}
                disabled={isCompleteStep && isSubmitting}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-3 text-sm font-semibold text-white transition-all hover:from-teal-600 hover:to-cyan-600 disabled:cursor-not-allowed disabled:opacity-60 sm:rounded-2xl sm:py-4 sm:text-base"
              >
                {isCompleteStep
                  ? copy.assessment.completeButton
                  : copy.assessment.nextButton}
                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>
          </div>
        </motion.div>
      </main>
    </div>
  );
}

function ResultsReportContent({
  copy,
  language,
  userInfo,
  finalScore,
  categoryScores,
  feedback,
  deepDiveContent,
}) {
  const overallScore = clampPercentage(finalScore.score, finalScore.max);
  const categories = buildReportCategories(
    categoryScores,
    deepDiveContent,
    feedback,
    language
  ).map((category, index) => ({
    ...category,
    color: RESULT_CARD_COLORS[index % RESULT_CARD_COLORS.length],
  }));

  const radarData = categories.map((item) => ({
    category: item.chartLabel,
    score: item.score,
  }));

  const barData = categories.map((item) => ({
    name: item.chartLabel,
    score: item.score,
  }));

  const icons = [Lightbulb, Shield, TrendingUp, ScaleIcon];

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h1 className="mb-3 text-2xl font-bold text-slate-800 sm:text-3xl md:text-4xl">
          {copy.results.title}
        </h1>
        <p className="text-sm text-slate-600 sm:text-base">
          {userInfo.fullName} • {userInfo.email}
        </p>
      </div>

      <section className="rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 p-6 text-white shadow-xl sm:rounded-3xl sm:p-8">
        <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
          <div className="text-center md:text-left">
            <h2 className="mb-2 text-lg font-semibold opacity-90 sm:text-xl">
              {copy.results.overallScore}
            </h2>
            <p className="max-w-md text-sm text-white/80 sm:text-base">
              {copy.results.overallScoreDesc}
            </p>
          </div>
          <div className="flex h-28 w-28 items-center justify-center rounded-full border-4 border-white/30 bg-white/20 backdrop-blur-sm sm:h-32 sm:w-32">
            <div className="text-center">
              <div className="text-4xl font-bold sm:text-5xl">{overallScore}</div>
              <div className="text-xs opacity-90 sm:text-sm">{copy.results.outOf}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 sm:gap-6">
        <div className="rounded-2xl bg-white p-4 shadow-lg shadow-slate-200/50 sm:rounded-3xl sm:p-6">
          <h3 className="mb-4 text-base font-semibold text-slate-800 sm:text-lg">
            {copy.results.categoryBreakdown}
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#cbd5e1" />
              <PolarAngleAxis dataKey="category" tick={{ fill: "#64748b", fontSize: 11 }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#64748b" }} />
              <Radar
                name="Score"
                dataKey="score"
                stroke="#14b8a6"
                fill="#2dd4bf"
                fillOpacity={0.6}
                isAnimationActive={false}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl bg-white p-4 shadow-lg shadow-slate-200/50 sm:rounded-3xl sm:p-6">
          <h3 className="mb-4 text-base font-semibold text-slate-800 sm:text-lg">
            {copy.results.scoreComparison}
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fill: "#64748b" }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                }}
              />
              <Bar
                dataKey="score"
                fill="#2dd4bf"
                radius={[8, 8, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-2 sm:gap-6">
        {categories.map((category, index) => {
          const Icon = icons[index % icons.length];
          return (
            <div
              key={category.category}
              className="rounded-2xl bg-white p-5 shadow-lg shadow-slate-200/50 sm:rounded-3xl sm:p-6"
            >
              <div className="mb-4 flex items-start gap-3 sm:gap-4">
                <div
                  className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${category.color} sm:h-12 sm:w-12`}
                >
                  <Icon className="h-5 w-5 text-white sm:h-6 sm:w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-1 text-base font-semibold text-slate-800 sm:text-lg">
                    {category.title}
                  </h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-bold text-slate-800 sm:text-2xl">
                      {category.score}
                    </span>
                    <span className="text-xs text-slate-500 sm:text-sm">
                      {copy.results.outOf}
                    </span>
                  </div>
                </div>
              </div>
              <div className="mb-3 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full bg-gradient-to-r ${category.color}`}
                  style={{ width: `${category.score}%` }}
                />
              </div>
              <p className="text-xs leading-relaxed text-slate-600 sm:text-sm">
                {category.description}
              </p>
            </div>
          );
        })}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-slate-50/80 p-5 sm:rounded-3xl sm:p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">
          {copy.results.strengthsTitle}
        </p>
        <p className="mt-3 text-sm leading-7 text-slate-700">{feedback?.message}</p>

        {feedback?.actions?.length ? (
          <div className="mt-6">
            <p className="text-sm font-semibold text-slate-900">
              {copy.results.nextStepsTitle}
            </p>
            <ul className="mt-3 space-y-4">
              {feedback.actions.map((action) => (
                <li key={`${action.label}-${action.href}`} className="text-sm text-slate-600">
                  <p>{action.text}</p>
                  <a
                    href={action.href}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-900 transition-colors hover:border-teal-200 hover:bg-teal-50"
                  >
                    {action.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </section>
    </div>
  );
}

function CompletionScreen({
  copy,
  showViewResults,
  autoEmailStatus,
  onViewResults,
}) {
  let body = copy.completion.savedNote;
  if (showViewResults) {
    body = copy.completion.resultsReady;
  } else if (autoEmailStatus === "error") {
    body = copy.completion.emailError;
  } else if (autoEmailStatus === "sending" || autoEmailStatus === "sent") {
    body = copy.completion.emailSent;
  }

  return (
    <div className="min-h-screen">
      <main className="flex min-h-[calc(100vh-8rem)] items-center justify-center px-4 py-12 sm:px-6">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-2xl text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="mb-6 inline-flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-teal-100 to-cyan-100 sm:mb-8 sm:h-24 sm:w-24"
          >
            <CheckCircle2 className="h-10 w-10 text-teal-600 sm:h-12 sm:w-12" />
          </motion.div>

          <h1 className="mb-4 text-2xl font-bold text-slate-800 sm:text-3xl md:text-4xl">
            {copy.completion.title}
          </h1>
          <p className="mb-8 text-base text-slate-600 sm:mb-12 sm:text-lg">
            {copy.completion.subtitle}
          </p>

          <div className="rounded-2xl bg-white p-6 shadow-lg shadow-slate-200/50 sm:rounded-3xl sm:p-8">
            {showViewResults ? (
              <>
                <p className="mb-6 text-sm text-slate-700 sm:text-base">{body}</p>
                <button
                  type="button"
                  onClick={onViewResults}
                  className="w-full rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-4 text-base font-semibold text-white transition-all hover:from-teal-600 hover:to-cyan-600 sm:rounded-2xl sm:py-5 sm:text-lg"
                >
                  {copy.completion.viewResultsButton}
                </button>
              </>
            ) : (
              <>
                <div className="mb-4 inline-flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 sm:h-16 sm:w-16">
                  <Mail className="h-7 w-7 text-blue-600 sm:h-8 sm:w-8" />
                </div>
                <p className="mb-2 text-sm text-slate-700 sm:text-base">{body}</p>
                <p className="text-xs text-slate-500 sm:text-sm">
                  {autoEmailStatus === "error"
                    ? copy.completion.savedNote
                    : copy.completion.emailNote}
                </p>
              </>
            )}
          </div>
        </motion.div>
      </main>

      <Footer copy={copy} />
    </div>
  );
}

function ResultsDashboard({
  copy,
  language,
  userInfo,
  finalScore,
  categoryScores,
  feedback,
  deepDiveContent,
  onContinue,
}) {
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-10 w-full border-b border-slate-200 bg-white/90 px-4 py-4 backdrop-blur-sm sm:px-6">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-teal-600 sm:h-6 sm:w-6" />
            <span className="text-sm font-semibold text-slate-800 sm:text-base">
              {copy.results.brand}
            </span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-lg shadow-slate-200/50 sm:p-8">
            <ResultsReportContent
              copy={copy}
              language={language}
              userInfo={userInfo}
              finalScore={finalScore}
              categoryScores={categoryScores}
              feedback={feedback}
              deepDiveContent={deepDiveContent}
            />
          </div>

          <div className="mt-8 flex justify-center">
            <button
              type="button"
              onClick={onContinue}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-4 text-sm font-semibold text-white transition-all hover:from-teal-600 hover:to-cyan-600 sm:rounded-2xl sm:px-8 sm:py-5 sm:text-base"
            >
              <Download className="h-4 w-4 sm:h-5 sm:w-5" />
              {copy.results.downloadButton}
            </button>
          </div>
        </motion.div>
      </main>

      <Footer copy={copy} />
    </div>
  );
}

function ReportDelivery({
  copy,
  email,
  allowDownload,
  allowEmail,
  emailState,
  onDownload,
  onEmail,
}) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50">
      <header className="w-full border-b border-slate-200 bg-white/90 px-4 py-4 backdrop-blur-sm sm:px-6">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <img src={kigumiLogo} alt="Kigumi Group" className="h-5 w-auto sm:h-6" />
        </div>
      </header>

      <main className="flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-2xl"
        >
          <div className="mb-6 text-center sm:mb-8">
            <h1 className="mb-3 text-2xl font-bold text-slate-800 sm:text-3xl md:text-4xl">
              {copy.delivery.title}
            </h1>
            <p className="text-sm text-slate-600 sm:text-base">{copy.delivery.subtitle}</p>
          </div>

          <div className="mb-6 flex items-start gap-3 rounded-xl border-2 border-amber-200 bg-amber-50 p-4 sm:mb-8 sm:rounded-2xl sm:p-5">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div>
              <p className="mb-1 text-sm font-medium text-amber-900">
                {copy.delivery.warningTitle}
              </p>
              <p className="text-xs text-amber-800 sm:text-sm">{copy.delivery.warningDesc}</p>
            </div>
          </div>

          <div className="mb-8 space-y-4">
            <ActionCard
              title={copy.delivery.downloadPDF}
              description={
                allowDownload ? copy.delivery.downloadPDFDesc : copy.delivery.unavailable
              }
              icon={<Download className="h-6 w-6 text-teal-600 sm:h-7 sm:w-7" />}
              iconTint="bg-gradient-to-br from-teal-100 to-cyan-100"
              disabled={!allowDownload}
              onClick={onDownload}
            />
            <ActionCard
              title={copy.delivery.emailReport}
              description={
                allowEmail
                  ? `${copy.delivery.emailReportDesc} ${email || ""}`
                  : copy.delivery.unavailable
              }
              icon={<Mail className="h-6 w-6 text-blue-600 sm:h-7 sm:w-7" />}
              iconTint="bg-gradient-to-br from-blue-100 to-indigo-100"
              disabled={!allowEmail || emailState === "sending"}
              onClick={onEmail}
            />
            {emailState === "sending" ? (
              <p className="text-sm text-slate-500">{copy.delivery.emailSending}</p>
            ) : null}
          </div>

          <p className="text-center text-xs text-slate-500 sm:text-sm">
            {copy.delivery.supportNote}
          </p>
        </motion.div>
      </main>

      <Footer copy={copy} />
    </div>
  );
}

function ActionCard({ title, description, icon, iconTint, disabled, onClick }) {
  const content = (
    <div
      className={`w-full rounded-xl bg-white p-5 shadow-lg shadow-slate-200/50 transition-all sm:rounded-2xl sm:p-6 ${
        disabled
          ? "cursor-not-allowed border-2 border-slate-200 opacity-60"
          : "border-2 border-transparent hover:border-teal-200 hover:shadow-xl"
      }`}
    >
      <div className="flex items-center gap-4">
        <div
          className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ${iconTint} sm:h-14 sm:w-14`}
        >
          {icon}
        </div>
        <div className="flex-1 text-left">
          <h3 className="mb-1 text-base font-semibold text-slate-800 sm:text-lg">{title}</h3>
          <p className="text-xs text-slate-600 sm:text-sm">{description}</p>
        </div>
      </div>
    </div>
  );

  if (disabled) return content;
  return (
    <motion.button type="button" onClick={onClick} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
      {content}
    </motion.button>
  );
}

function normalizeOptionLabel(option) {
  if (typeof option === "string") return option;
  return option.answer || option.value || String(option);
}

function normalizeOptionValue(option) {
  if (typeof option === "string") return option;
  return option.value || option.answer || String(option);
}

function calculateResults(questions, answers) {
  let totalScore = 0;
  let maxScore = 0;
  const categoryTotals = {};

  questions.forEach((question) => {
    if (question.meta) return;

    let score = 0;
    let possible = 0;

    if (question.scoring) {
      score = question.scoring(answers) || 0;
      possible = question.maxPoints || 3;
    } else if (question.type === "likert") {
      const value = answers[question.name];
      if (value !== undefined && question.options[value]) {
        score = question.options[value].points ?? 0;
        possible = Math.max(
          ...question.options.map((option) => option.points ?? 0),
          1
        );
      }
    } else if (question.type === "grouped_likert") {
      question.subQuestions.forEach((subQuestion) => {
        const value = answers[subQuestion.name];
        if (value !== undefined && question.options[value]) {
          score += question.options[value].points ?? 0;
          possible += Math.max(
            ...question.options.map((option) => option.points ?? 0),
            1
          );
        }
      });
    }

    totalScore += score;
    maxScore += possible;

    if (!categoryTotals[question.category]) {
      categoryTotals[question.category] = { score: 0, max: 0 };
    }
    categoryTotals[question.category].score += score;
    categoryTotals[question.category].max += possible;
  });

  const categoryScores = Object.fromEntries(
    Object.entries(categoryTotals).map(([category, scoreData]) => [
      category,
      {
        score: scoreData.score,
        max: scoreData.max > 0 ? scoreData.max : 1,
      },
    ])
  );

  return {
    finalScore: {
      score: totalScore,
      max: maxScore > 0 ? maxScore : 1,
    },
    categoryScores,
  };
}

function validateAssessmentQuestion(questions, step, answers, copy) {
  const currentQuestion = questions[step];
  let isValid = false;

  if (currentQuestion.type === "grouped_likert") {
    isValid = currentQuestion.subQuestions.every(
      (subQuestion) =>
        answers[subQuestion.name] !== undefined && answers[subQuestion.name] !== null
    );
  } else if (currentQuestion.type === "checkbox") {
    isValid = (answers[currentQuestion.name] || []).length > 0;
  } else if (currentQuestion.type === "likert") {
    isValid =
      answers[currentQuestion.name] !== undefined &&
      answers[currentQuestion.name] !== null;
  } else if (currentQuestion.type === "ranking") {
    isValid =
      Array.isArray(answers[currentQuestion.name]) &&
      answers[currentQuestion.name].length > 0;
  } else {
    isValid =
      answers[currentQuestion.name] !== undefined &&
      String(answers[currentQuestion.name]).trim() !== "";
  }

  if (!isValid) return copy.assessment.errorAnswerRequired;

  const visibleNumber = step + 1;
  if ([3, 21, 27, 30].includes(visibleNumber)) {
    const questionName = currentQuestion.name;
    const hasOtherSelected =
      (currentQuestion.type === "checkbox" &&
        (answers[questionName] || []).some((value) => includesOther(value))) ||
      (currentQuestion.type === "radio" &&
        answers[questionName] &&
        includesOther(answers[questionName]));

    if (hasOtherSelected) {
      const otherText = (answers[`${questionName}_other`] || "").trim();
      if (!otherText) return copy.assessment.errorOtherRequired;
    }
  }

  return "";
}

export default function DiagnosticExperience() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const isInviteEntry = location.pathname.startsWith("/start");
  const searchParams = new URLSearchParams(location.search);
  const requestedVariant = searchParams.get("variant") === "pre" ? "pre" : "post";

  const initialLanguage = getRequestedLanguage(location.search, params.lang);
  const initialInviteCode = normalizeInviteCode(searchParams.get("code"));
  const [language, setLanguage] = useState(initialLanguage);
  const [currentStep, setCurrentStep] = useState("welcome");
  const [inviteConfig, setInviteConfig] = useState(null);
  const [inviteStatus, setInviteStatus] = useState(
    initialInviteCode ? "loading" : "idle"
  );
  const [inviteError, setInviteError] = useState("");
  const [userInfo, setUserInfo] = useState({
    fullName: "",
    email: "",
    inviteCode: initialInviteCode,
    studentClass: "",
    role: "school",
    language: initialLanguage,
    termsAccepted: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [questionIndex, setQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [assessmentError, setAssessmentError] = useState("");
  const [resultsState, setResultsState] = useState({
    finalScore: null,
    categoryScores: {},
    feedback: null,
    submissionStatus: "idle",
    autoEmailStatus: "idle",
  });

  const autoEmailRequestedRef = useRef(false);
  const finalSubmitInFlightRef = useRef(false);

  const copy = getExperienceCopy(language);
  const localeBundle = DIAGNOSTIC_BUNDLES[language] || DIAGNOSTIC_BUNDLES.en;
  const assessmentQuestions = useMemo(
    () => localeBundle.questions.filter((question) => !question.meta),
    [localeBundle.questions]
  );
  const effectiveInviteConfig = useMemo(
    () =>
      inviteConfig || {
        ...DEFAULT_INVITE_CONFIG,
        code: userInfo.inviteCode,
        role: userInfo.role,
        assessmentType: userInfo.role,
        assessmentVersion: requestedVariant === "pre" ? "baseline" : "post",
        flow: requestedVariant,
        showImmediateResults: requestedVariant !== "pre",
        allowDownload: requestedVariant !== "pre",
      },
    [inviteConfig, requestedVariant, userInfo.inviteCode, userInfo.role]
  );
  const reportPdfData = useMemo(() => {
    if (!resultsState.finalScore || !resultsState.feedback) {
      return null;
    }

    return {
      copy,
      language,
      userInfo,
      inviteConfig: effectiveInviteConfig,
      finalScore: resultsState.finalScore,
      categoryScores: resultsState.categoryScores,
      feedback: resultsState.feedback,
      deepDiveContent: localeBundle.deepDiveContent,
    };
  }, [
    copy,
    effectiveInviteConfig,
    language,
    localeBundle.deepDiveContent,
    resultsState.categoryScores,
    resultsState.feedback,
    resultsState.finalScore,
    userInfo,
  ]);

  useEffect(() => {
    localStorage.setItem("lang", language);
  }, [language]);

  useEffect(() => {
    const nextLanguage = getRequestedLanguage(location.search, params.lang);

    setLanguage((currentLanguage) =>
      currentLanguage === nextLanguage ? currentLanguage : nextLanguage
    );

    setUserInfo((currentUserInfo) =>
      currentUserInfo.language === nextLanguage
        ? currentUserInfo
        : { ...currentUserInfo, language: nextLanguage }
    );
  }, [location.search, params.lang]);

  useEffect(() => {
    document.title = "Diagnostic Test - Family Cybersafety and Digital Well-being";
  }, []);

  useEffect(() => {
    const currentQuestion = assessmentQuestions[questionIndex];
    if (
      currentStep === "assessment" &&
      currentQuestion?.type === "ranking" &&
      !Array.isArray(answers[currentQuestion.name])
    ) {
      setAnswers((currentAnswers) => ({
        ...currentAnswers,
        [currentQuestion.name]: [...currentQuestion.options],
      }));
    }
  }, [answers, assessmentQuestions, currentStep, questionIndex]);

  useEffect(() => {
    const nextParams = new URLSearchParams(location.search);
    if (userInfo.inviteCode) {
      nextParams.set("code", userInfo.inviteCode);
    } else {
      nextParams.delete("code");
    }
    nextParams.set("lang", language);
    const nextSearch = nextParams.toString();
    const currentSearch = location.search.replace(/^\?/, "");
    if (nextSearch !== currentSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true }
      );
    }
  }, [language, location.pathname, location.search, navigate, userInfo.inviteCode]);

  useEffect(() => {
    const code = normalizeInviteCode(userInfo.inviteCode);
    if (!code) {
      setInviteConfig(null);
      setInviteStatus("idle");
      setInviteError("");
      return;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      try {
        setInviteStatus("loading");
        setInviteError("");
        const response = await fetch(apiUrl(`/invite-codes/${encodeURIComponent(code)}`));
        if (!response.ok) {
          throw new Error(copy.landing.inviteCodeInvalid);
        }

        const payload = await response.json();
        if (cancelled) return;
        setInviteConfig(payload.inviteConfig);
        setInviteStatus("resolved");
        setInviteError("");
        setUserInfo((currentUserInfo) => ({
          ...currentUserInfo,
          inviteCode: payload.inviteConfig.code,
          role: payload.inviteConfig.role,
        }));
      } catch (error) {
        if (cancelled) return;
        setInviteConfig(null);
        setInviteStatus("error");
        setInviteError(error.message || copy.landing.inviteCodeInvalid);
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [copy.landing.inviteCodeInvalid, userInfo.inviteCode]);

  useEffect(() => {
    if (
      currentStep !== "completion" ||
      effectiveInviteConfig.showImmediateResults ||
      !effectiveInviteConfig.allowEmail ||
      !resultsState.finalScore ||
      autoEmailRequestedRef.current
    ) {
      return;
    }

    autoEmailRequestedRef.current = true;

    const send = async () => {
      try {
        setResultsState((current) => ({ ...current, autoEmailStatus: "sending" }));
        await new Promise((resolve) => window.setTimeout(resolve, 500));
        if (!reportPdfData || !userInfo.email) {
          throw new Error("Missing report or email");
        }

        const pdfBlob = await buildReportPdfBlob(reportPdfData);
        const formData = new FormData();
        formData.append("email", userInfo.email);
        formData.append("lang", language);
        formData.append("pdf", pdfBlob, PDF_FILENAME);

        const response = await fetch(apiUrl("/email/send"), {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          throw new Error("Failed to send email");
        }

        setResultsState((current) => ({ ...current, autoEmailStatus: "sent" }));
      } catch (error) {
        console.error("Auto email failed:", error);
        setResultsState((current) => ({ ...current, autoEmailStatus: "error" }));
      }
    };

    send();
  }, [
    currentStep,
    effectiveInviteConfig.allowEmail,
    effectiveInviteConfig.showImmediateResults,
    language,
    reportPdfData,
    resultsState.finalScore,
    userInfo.email,
  ]);

  const handleUserInfoChange = (field, value) => {
    setFormErrors((currentErrors) => ({ ...currentErrors, [field]: "" }));
    setInviteError("");

    if (field === "language") {
      setUserInfo((currentUserInfo) => ({ ...currentUserInfo, language: value }));
      return;
    }

    setUserInfo((currentUserInfo) => ({ ...currentUserInfo, [field]: value }));
  };

  const goToForm = () => {
    startTransition(() => setCurrentStep("form"));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!userInfo.fullName.trim()) {
      nextErrors.fullName = copy.form.errors.nameRequired;
    }

    if (!userInfo.email.trim()) {
      nextErrors.email = copy.form.errors.emailRequired;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userInfo.email)) {
      nextErrors.email = copy.form.errors.emailInvalid;
    }

    if (isInviteEntry && inviteStatus !== "resolved") {
      nextErrors.inviteCode = copy.form.errors.inviteRequired;
    }

    if (!userInfo.termsAccepted) {
      nextErrors.terms = copy.form.errors.termsRequired;
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleAssessmentAdvance = () => {
    const nextError = validateAssessmentQuestion(
      assessmentQuestions,
      questionIndex,
      answers,
      copy
    );
    if (nextError) {
      setAssessmentError(nextError);
      return;
    }
    setAssessmentError("");
    startTransition(() => setQuestionIndex((currentQuestionIndex) => currentQuestionIndex + 1));
  };

  const handleFinalSubmit = async () => {
    if (finalSubmitInFlightRef.current) {
      return;
    }

    const nextError = validateAssessmentQuestion(
      assessmentQuestions,
      questionIndex,
      answers,
      copy
    );
    if (nextError) {
      setAssessmentError(nextError);
      return;
    }

    finalSubmitInFlightRef.current = true;

    const completeAnswers = {
      ...answers,
      first_name: userInfo.fullName,
      email_address: userInfo.email,
      invite_code: userInfo.inviteCode,
      assessment_role: userInfo.role,
      student_class: userInfo.studentClass,
      termsAccepted: userInfo.termsAccepted,
    };

    const { finalScore, categoryScores } = calculateResults(
      localeBundle.questions,
      completeAnswers
    );
    const feedback = buildFeedbackResult(finalScore, localeBundle.feedbackMessages);

    setResultsState({
      finalScore,
      categoryScores,
      feedback,
      submissionStatus: "saving",
      autoEmailStatus: "idle",
    });

    autoEmailRequestedRef.current = false;

    try {
      const payload = {
        flow: effectiveInviteConfig.flow || "post",
        locale: language,
        first_name: userInfo.fullName,
        email_address: userInfo.email,
        inviteCode: userInfo.inviteCode,
        inviteConfig: effectiveInviteConfig,
        role: userInfo.role,
        studentClass: userInfo.studentClass || null,
        assessmentVersion: effectiveInviteConfig.assessmentVersion,
        questionSet: effectiveInviteConfig.questionSet,
        answers: completeAnswers,
        finalScore,
        categoryScores,
        submittedAt: new Date(),
      };

      const response = await fetch(apiUrl("/responses"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error("Failed to save response");
      }

      setResultsState((current) => ({ ...current, submissionStatus: "saved" }));
    } catch (error) {
      console.error("Error saving response:", error);
      setResultsState((current) => ({ ...current, submissionStatus: "error" }));
    }

    startTransition(() => setCurrentStep("completion"));
  };

  const handleDownloadReport = async () => {
    if (!reportPdfData) return;
    await downloadPdfBlob(reportPdfData);
  };

  const handleEmailReport = async () => {
    try {
      setResultsState((current) => ({ ...current, autoEmailStatus: "sending" }));
      if (!reportPdfData) {
        throw new Error("Report is not ready");
      }

      const pdfBlob = await buildReportPdfBlob(reportPdfData);
      const formData = new FormData();
      formData.append("email", userInfo.email);
      formData.append("lang", language);
      formData.append("pdf", pdfBlob, PDF_FILENAME);

      const response = await fetch(apiUrl("/email/send"), {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error("Failed to send email");
      }

      setResultsState((current) => ({ ...current, autoEmailStatus: "sent" }));
      window.alert(`PDF emailed successfully to ${userInfo.email}`);
    } catch (error) {
      console.error("Error sending email:", error);
      setResultsState((current) => ({ ...current, autoEmailStatus: "error" }));
      window.alert(`Error sending email: ${error.message}`);
    }
  };

  if (currentStep === "welcome") {
    return (
      <WelcomeScreen
        copy={copy}
        language={language}
        setLanguage={setLanguage}
        onContinue={goToForm}
        isInviteEntry={isInviteEntry}
        inviteStatus={inviteStatus}
        inviteError={inviteError}
        disableContinue={isInviteEntry && inviteStatus !== "resolved"}
      />
    );
  }

  if (currentStep === "form") {
    return (
      <BasicInfoForm
        copy={copy}
        language={language}
        userInfo={userInfo}
        formErrors={formErrors}
        inviteStatus={inviteStatus}
        inviteError={inviteError}
        inviteCodeLocked={Boolean(initialInviteCode)}
        roleLocked={Boolean(inviteConfig?.role)}
        requireInviteCode={isInviteEntry}
        onBack={() => startTransition(() => setCurrentStep("welcome"))}
        onChange={handleUserInfoChange}
        onSubmit={() => {
          if (!validateForm()) return;
          setLanguage(normalizeLanguage(userInfo.language));
          setAssessmentError("");
          startTransition(() => setCurrentStep("assessment"));
        }}
      />
    );
  }

  if (currentStep === "assessment") {
    return (
      <AssessmentScreen
        copy={copy}
        currentQuestion={assessmentQuestions[questionIndex]}
        currentIndex={questionIndex}
        totalQuestions={assessmentQuestions.length}
        answers={answers}
        error={assessmentError}
        onBack={() => {
          setAssessmentError("");
          startTransition(() =>
            setQuestionIndex((currentQuestionIndex) => currentQuestionIndex - 1)
          );
        }}
        onNext={handleAssessmentAdvance}
        onComplete={handleFinalSubmit}
        isSubmitting={resultsState.submissionStatus === "saving"}
        onAnswerChange={(field, value) => {
          setAssessmentError("");
          setAnswers((currentAnswers) => ({ ...currentAnswers, [field]: value }));
        }}
      />
    );
  }

  if (currentStep === "completion") {
    return (
      <CompletionScreen
        copy={copy}
        showViewResults={effectiveInviteConfig.showImmediateResults}
        autoEmailStatus={resultsState.autoEmailStatus}
        onViewResults={() => startTransition(() => setCurrentStep("results"))}
      />
    );
  }

  if (currentStep === "results" && resultsState.finalScore && resultsState.feedback) {
    return (
      <ResultsDashboard
        copy={copy}
        language={language}
        userInfo={userInfo}
        finalScore={resultsState.finalScore}
        categoryScores={resultsState.categoryScores}
        feedback={resultsState.feedback}
        deepDiveContent={localeBundle.deepDiveContent}
        onContinue={() => startTransition(() => setCurrentStep("delivery"))}
      />
    );
  }

  if (currentStep === "delivery") {
    return (
      <ReportDelivery
        copy={copy}
        email={userInfo.email}
        allowDownload={effectiveInviteConfig.allowDownload}
        allowEmail={effectiveInviteConfig.allowEmail}
        emailState={resultsState.autoEmailStatus}
        onDownload={handleDownloadReport}
        onEmail={handleEmailReport}
      />
    );
  }

  return null;
}
